export type OrsManeuverType =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14;

type ManeuverKind =
  | 'left'
  | 'right'
  | 'sharp-left'
  | 'sharp-right'
  | 'slight-left'
  | 'slight-right'
  | 'straight'
  | 'keep-left'
  | 'keep-right'
  | 'uturn'
  | 'roundabout'
  | 'roundabout-exit'
  | 'arrive'
  | 'depart'
  | 'unknown';

const ORS_KIND: Record<number, ManeuverKind> = {
  0: 'left',
  1: 'right',
  2: 'sharp-left',
  3: 'sharp-right',
  4: 'slight-left',
  5: 'slight-right',
  6: 'straight',
  7: 'roundabout',
  8: 'roundabout-exit',
  9: 'uturn',
  10: 'arrive',
  11: 'depart',
  12: 'keep-left',
  13: 'keep-right',
  14: 'unknown',
};

const ICON_PATHS: Record<ManeuverKind, string> = {
  straight: '<path d="M12 19V5M12 5l-4 4M12 5l4 4"/>',
  left: '<path d="M19 12H5M5 12l4-4M5 12l4 4"/>',
  right: '<path d="M5 12h14M19 12l-4-4M19 12l-4 4"/>',
  'sharp-left': '<path d="M18 6H8a4 4 0 0 0-4 4v8M4 18l3-3M4 18l3 3"/>',
  'sharp-right': '<path d="M6 6h10a4 4 0 0 1 4 4v8M20 18l-3-3M20 18l-3 3"/>',
  'slight-left': '<path d="M16 18H8a3 3 0 0 1-3-3V9M5 9l-2 2M5 9l2 2"/>',
  'slight-right': '<path d="M8 18h8a3 3 0 0 0 3-3V9M19 9l2 2M19 9l-2 2"/>',
  'keep-left':
    '<path d="M12 21V13"/><path d="M12 13l7-9" opacity="0.28" stroke-width="1.85"/><path d="M12 13L5 4" stroke-width="2.85"/><path d="M5 4l4.2.9L7.2 8.6Z" fill="currentColor" stroke="none"/>',
  'keep-right':
    '<path d="M12 21V13"/><path d="M12 13L5 4" opacity="0.28" stroke-width="1.85"/><path d="M12 13l7-9" stroke-width="2.85"/><path d="M19 4l-4.2.9L16.8 8.6Z" fill="currentColor" stroke="none"/>',
  uturn: '<path d="M9 14V8a4 4 0 0 1 4-4h2M15 4l2 2M15 4l-2 2M9 14l-2-2M9 14l2-2"/>',
  roundabout:
    '<circle cx="12" cy="14" r="5"/><path d="M12 9V5M12 5l-2 2M12 5l2 2"/>',
  'roundabout-exit':
    '<circle cx="12" cy="13" r="5"/><path d="M17 13h3M20 13l-2-2M20 13l-2 2"/>',
  arrive: '<circle cx="12" cy="11" r="3"/><path d="M12 14v7"/>',
  depart: '<circle cx="12" cy="19" r="2" fill="currentColor" stroke="none"/><path d="M12 17V5M12 5l-3 3M12 5l3 3"/>',
  unknown: '<path d="M12 19V5M12 5l-4 4M12 5l4 4"/>',
};

export function maneuverKind(
  type: number | undefined,
  instruction = '',
): ManeuverKind {
  if (type !== undefined && Number.isFinite(type) && type in ORS_KIND) {
    return ORS_KIND[type]!;
  }
  return kindFromInstruction(instruction);
}

function kindFromInstruction(instruction: string): ManeuverKind {
  const s = instruction.toLowerCase();
  if (/u-?\s*turn|kehre/.test(s)) return 'uturn';
  if (/sharp\s+left|scharf\s+links/.test(s)) return 'sharp-left';
  if (/sharp\s+right|scharf\s+rechts/.test(s)) return 'sharp-right';
  if (/slight\s+left|halb\s+links|bear\s+left/.test(s)) return 'slight-left';
  if (/slight\s+right|halb\s+rechts|bear\s+right/.test(s)) return 'slight-right';
  if (/keep\s+left/.test(s)) return 'keep-left';
  if (/keep\s+right/.test(s)) return 'keep-right';
  if (/\bleft\b|links/.test(s)) return 'left';
  if (/\bright\b|rechts/.test(s)) return 'right';
  if (/roundabout|kreisverkehr/.test(s)) return 'roundabout';
  if (/exit.*roundabout/.test(s)) return 'roundabout-exit';
  if (/arrive|destination|ziel/.test(s)) return 'arrive';
  if (/depart|head/.test(s)) return 'depart';
  return 'straight';
}

export function maneuverArrowSvg(
  type: number | undefined,
  instruction = '',
): string {
  const kind = maneuverKind(type, instruction);
  const paths = ICON_PATHS[kind];
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}

export function maneuverStreetLine(
  instruction: string,
  name: string,
): string | null {
  const street = name.trim();
  if (!street) return null;
  if (instruction.toLowerCase().includes(street.toLowerCase())) return null;
  return street;
}

/** Short “Then …” line for the upcoming maneuver under the banner. */
export function maneuverThenLabel(instruction: string): string {
  const raw = instruction.trim();
  if (!raw) return '';
  const cut = raw.split(/\s+onto\s+/i)[0]?.trim() || raw;
  if (cut.length <= 40) return cut;
  return `${cut.slice(0, 38)}\u2026`;
}

/**
 * Lane / keep / roundabout-exit chip. Returns null when ORS gives nothing
 * actionable beyond the main instruction (most steps).
 */
export function laneHintFromStep(
  type: number | undefined,
  instruction = '',
  exitNumber?: number,
): 'keep-left' | 'keep-right' | 'exit' | null {
  const exit = resolveRoundaboutExit(type, instruction, exitNumber);
  if (exit !== null) return 'exit';
  const kind = maneuverKind(type, instruction);
  if (kind === 'keep-left') return 'keep-left';
  if (kind === 'keep-right') return 'keep-right';
  return null;
}

/** Prefer API exit_number; fall back to “take the Nth exit” in the text. */
export function resolveRoundaboutExit(
  type: number | undefined,
  instruction = '',
  exitNumber?: number,
): number | null {
  const isRoundabout = type === 7 || type === 8 || /roundabout|kreisverkehr/i.test(instruction);
  if (!isRoundabout) return null;
  if (exitNumber !== undefined && Number.isFinite(exitNumber) && exitNumber > 0) {
    return Math.round(exitNumber);
  }
  const match = instruction.match(
    /(\d+)\s*(?:st|nd|rd|th)?\s+exit|exit\s*(?:number\s*)?(\d+)/i,
  );
  if (!match) return null;
  const n = Number(match[1] ?? match[2]);
  return Number.isFinite(n) && n > 0 ? n : null;
}
